import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { toast } from 'sonner'

import { OnboardingBannerSlot } from '@/components/OnboardingBannerSlot'
import { SkeletonRows } from '@/components/ui/skeleton'
import { UploadBlankState } from '@/components/ui/upload-blank-state'
import { apiErrorDetail } from '@/lib/apiError'
import { DocumentsTable } from '../../../components/documents/DocumentsTable'
import { UploadDocumentButton } from '../../../components/documents/UploadDocumentButton'
import { UploadDocumentDialog } from '../../../components/documents/UploadDocumentDialog'
import { DocumentType, useOrgDocuments, useUploadDocument } from '../../../models/document'
import { continuesApplication, useCurrentUser } from '../../../models/user'

export function Documents() {
  const { user } = useCurrentUser({ enabled: true })
  const orgId = user?.currentOrg?.id
  const { documents, isLoading } = useOrgDocuments(orgId)
  const [dialogOpen, setDialogOpen] = useState(false)
  const upload = useUploadDocument(orgId ?? '')
  const addDisabled = continuesApplication(user)

  // Customers can only upload Other, so a dropped file needs no type picked.
  function uploadDropped(file: File) {
    upload.mutate(
      { file, documentType: DocumentType.OTHER, folder: null },
      {
        onSuccess: () => toast.success('Document uploaded.'),
        onError: (error) => toast.error(apiErrorDetail(error, 'Could not upload that file.')),
      }
    )
  }

  function renderContent() {
    if (isLoading) {
      return <SkeletonRows />
    }

    if (documents.length === 0) {
      return (
        <UploadBlankState
          addLabel="Add document"
          onAdd={() => setDialogOpen(true)}
          onFile={uploadDropped}
          accept=".pdf,.xlsx,.xls,.csv,.tsv,.txt,.docx,.doc,.png,.jpg,.jpeg,.gif,.webp"
          isLoading={upload.isPending}
          disabled={addDisabled || !orgId}
        />
      )
    }

    return <DocumentsTable documents={documents} />
  }

  return (
    <>
      <Helmet>
        <title>Documents</title>
      </Helmet>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-ink text-2xl font-bold tracking-[-0.01em]">Documents</h1>
        {orgId && documents.length > 0 && <UploadDocumentButton orgId={orgId} disabled={addDisabled} />}
      </div>
      <div className="flex flex-col gap-6">
        <OnboardingBannerSlot />
        {renderContent()}
      </div>
      {dialogOpen && orgId && <UploadDocumentDialog orgId={orgId} onClose={() => setDialogOpen(false)} />}
    </>
  )
}
